import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

type ProductState = {
  [key: string]: string;
} & {
  image?: string;
};

type ProductContextType = {
  state: ProductState;
  updateOption: (name: string, value: string) => ProductState;
  updateImage: (index: string) => ProductState;
};

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export function ProductProvider({ children }: { children: React.ReactNode }) {
  const [searchParams] = useSearchParams();

  const getInitialState = (): ProductState => {
    const params: ProductState = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  };

  const [state, setState] = useState<ProductState>(getInitialState());

  useEffect(() => {
    setState(getInitialState());
  }, [searchParams]);

  const updateOption = (name: string, value: string) => {
    const newState = { ...state, [name]: value };
    setState(newState);
    return newState;
  };

  const updateImage = (index: string) => {
    const newState = { ...state, image: index };
    setState(newState);
    return newState;
  };

  const value = useMemo(
    () => ({
      state,
      updateOption,
      updateImage
    }),
    [state]
  );

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
}

export function useProduct() {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error('useProduct must be used within a ProductProvider');
  }
  return context;
}

export function useUpdateURL() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  return (state: ProductState) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(state).forEach(([key, value]) => {
      newParams.set(key, value);
    });
    navigate(`?${newParams.toString()}`, { replace: true });
  };
}



